import { useState, useEffect } from 'react';
import { getCenterConfig, saveCenterConfig } from '../../lib/db';
import { REPORT_CARD_THEMES } from '../../lib/themes';
import { setLanguage } from '../../lib/i18n';

interface CenterConfiguration {
  id: string;
  name: string;
  logo?: string;
  isCra?: boolean;
  reportCardTheme?: string;
  language?: string;
}

export function CenterConfig() {
  const [config, setConfig] = useState<CenterConfiguration>({
    id: crypto.randomUUID(),
    name: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'unsaved' | 'saved'>('saved');

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const centerConfig = await getCenterConfig();
        if (centerConfig) {
          setConfig(centerConfig);
        }
      } catch (error) {
        console.error('Error loading center config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setSaveStatus('unsaved');
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
        
        setConfig(prev => ({
          ...prev,
          logo: base64
        }));
        setSaveStatus('unsaved');
      } catch (error) {
        console.error('Error al cargar el logo:', error);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveCenterConfig(config);
      if (config.language) {
        setLanguage(config.language as 'es' | 'ca' | 'va');
      }
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error saving center config:', error);
      alert('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="mb-6 text-xl font-semibold text-gray-800">Configuración del Centro</h2>
      
      <div className="space-y-6">
        {/* Nombre del centro */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Nombre del Centro
          </label>
          <input
            type="text"
            name="name"
            value={config.name}
            onChange={handleChange}
            className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Nombre del centro educativo"
          />
        </div>

        {/* CRA */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => { setConfig(prev => ({ ...prev, isCra: !prev.isCra })); setSaveStatus('unsaved'); }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.isCra ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.isCra ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Centro Rural Agrupado (CRA)</span>
              <p className="text-xs text-gray-500">Activa la gestión multi-pueblo: pueblos, asignación de asignaturas por pueblo y filtrado de profesores.</p>
            </div>
          </label>
        </div>

        {/* Logo del centro */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Logo del Centro
          </label>
          <div className="flex items-start space-x-4">
            {config.logo ? (
              <div className="relative w-40 h-40">
                <img 
                  src={config.logo} 
                  alt="Logo del centro"
                  className="object-contain w-full h-full rounded-lg border"
                />
                <button
                  title="Eliminar logo"
                  onClick={() => {
                    setConfig(prev => ({ ...prev, logo: undefined }));
                    setSaveStatus('unsaved');
                  }}
                  className="absolute top-2 right-2 p-1 text-red-600 bg-white rounded-full hover:bg-red-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex justify-center items-center w-40 h-40 bg-gray-50 rounded-lg border-2 border-gray-300 border-dashed">
                <label className="flex flex-col items-center cursor-pointer">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="mt-2 text-sm text-gray-500">Subir logo</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Tema del boletín */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Tema del Boletín de Notas
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.values(REPORT_CARD_THEMES).map(theme => (
              <button
                key={theme.id}
                type="button"
                onClick={() => { setConfig(prev => ({ ...prev, reportCardTheme: theme.id })); setSaveStatus('unsaved'); }}
                className={`relative flex flex-col p-3 rounded-lg border-2 transition-all ${
                  config.reportCardTheme === theme.id
                    ? 'border-indigo-500 ring-2 ring-indigo-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: theme.colors.primary }} />
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.colors.accent }} />
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.colors.sectionBg }} />
                </div>
                <span className="text-sm font-medium text-gray-800">{theme.name}</span>
                <span className="text-xs text-gray-500">{theme.description}</span>
                {config.reportCardTheme === theme.id && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Idioma */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Idioma del Centro
          </label>
          <div className="relative">
            <select
              value={config.language || 'es'}
              onChange={e => { setConfig(prev => ({ ...prev, language: e.target.value })); setSaveStatus('unsaved'); }}
              className="block w-full appearance-none rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-9 text-sm font-medium text-slate-800 shadow-sm hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition-all duration-150 cursor-pointer"
            >
              <option value="es">Castellano</option>
              <option value="ca">Catalán</option>
              <option value="va">Valenciano</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de guardar */}
      <div className="flex justify-end mt-6">
        <button
          onClick={handleSave}
          disabled={saving || saveStatus === 'saved'}
          className={`
            px-4 py-2 font-medium text-white rounded-md
            ${saveStatus === 'saved'
              ? 'bg-green-600'
              : 'bg-indigo-600 hover:bg-indigo-700'
            }
            ${saving ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {saving ? 'Guardando...' : saveStatus === 'saved' ? 'Guardado' : 'Guardar Cambios'}
        </button>
      </div>
    </div>
  );
} 